import { useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * SessionHeartbeat Component
 * 
 * Periodically pings the server when a user session is active (ssoToken present).
 * Keeps network connections active in isolated browser proxies (e.g., Mimecast RBI)
 * to prevent remote container idle timeouts and session expiration popups.
 */
const SessionHeartbeat = () => {
  const isPingingRef = useRef(false);

  useEffect(() => {
    const PING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

    const sendHeartbeat = async () => {
      const token = sessionStorage.getItem("ssoToken");
      if (!token) return;

      // Avoid overlapping requests if a previous ping is still in flight
      if (isPingingRef.current) return;

      isPingingRef.current = true;
      const apiUrl = global.platformURI || "";

      try {
        await axios.get(`${apiUrl}/api/v1/users/fetch_user`, {
          headers: {
            Authorization: token,
          },
          timeout: 10000, // 10 second timeout to prevent Chrome socket stalling (2.2 min delays)
        });
      } catch (err) {
        // Silently catch background ping network/timeout errors
      } finally {
        isPingingRef.current = false;
      }
    };

    // Send an immediate heartbeat on mount if session exists
    sendHeartbeat();

    const intervalId = setInterval(sendHeartbeat, PING_INTERVAL_MS);

    // Send heartbeat when tab becomes visible after backgrounding
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
};

export default SessionHeartbeat;
